import { useCallback, useMemo } from "react"

import { useQuery, useQueryClient } from "@tanstack/react-query"

interface UseSaveYearProps {
  id: string
}

interface UseSaveYearValue {
  saveYear: (year: number) => void
  savedYear: number | null | undefined
}

/**
 * Manages the year in the persistence layer.
 *
 * @example
 * const {
 *   savedYear,
 *   saveYear,
 * } = useSaveYear({
 *   id: "sitesPerformanceYear",
 * })
 */
export const useSaveYear = ({ id }: UseSaveYearProps): UseSaveYearValue => {
  const queryClient = useQueryClient()
  const queryKey = useMemo(() => [id], [id])

  const { data } = useQuery({
    gcTime: Infinity,
    queryFn: async (): Promise<number | null> =>
      queryClient.getQueryData(queryKey) ?? null,
    queryKey,
    staleTime: Infinity,
  })

  const saveYear = useCallback(
    (year: number) => {
      queryClient.setQueryData(queryKey, year)
      void queryClient.invalidateQueries({ queryKey })
    },
    [queryClient, queryKey]
  )

  return {
    saveYear,
    savedYear: data,
  }
}
